import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseMarkdown } from '../../lib/leckeMarkdown';
import './lecke.css';

/* ══════════════════════════════════════════════════════════════════════════
   A lecke markdown-leírása (Tananyag 1. kör). Az elemzés a lib/leckeMarkdown-ban;
   itt csak React-elemek születnek (innerHTML nincs, így nyers HTML nem renderelődik).
   Képek: 700 px alatt a mobilváltozat (<picture>), késleltetett betöltés width/height-tel
   (nem ugrik az oldal), koppintásra nagyítva nyílik; a képaláírás halványabb, kisebb.
   Belső linkek (/gyakorlo, /alakzatfelismero, /gyertyafelismero) az alkalmazáson belül.
   ══════════════════════════════════════════════════════════════════════════ */

function Inline({ c }) {
  return c.map((n, k) => {
    if (n.t === 'text') return <Fragment key={k}>{n.v}</Fragment>;
    if (n.t === 'b') return <strong key={k}><Inline c={n.c} /></strong>;
    if (n.t === 'i') return <em key={k}><Inline c={n.c} /></em>;
    if (n.belso) return <Link key={k} to={n.to} className="lmd-a"><Inline c={n.c} /></Link>;
    return <a key={k} href={n.to} target="_blank" rel="noopener noreferrer" className="lmd-a"><Inline c={n.c} /></a>;
  });
}

function Nagyito({ kep, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="lmd-lb" role="dialog" aria-modal="true" aria-label={alt || 'Kép nagyítva'} onClick={onClose}>
      <img src={kep.src} alt={alt} width={kep.w || undefined} height={kep.h || undefined} />
      <button type="button" className="lmd-lb-x" aria-label="Bezárás" onClick={onClose}>✕</button>
    </div>
  );
}

function Abra({ b, onOpen }) {
  const { kep, alt } = b;
  return (
    <figure className="lmd-fig">
      <button type="button" className="lmd-fig-btn" onClick={() => onOpen(b)} aria-label={`Nagyítás: ${alt}`}>
        <picture>
          {kep.mobil && <source media="(max-width: 699px)" srcSet={kep.mobil.src} width={kep.mobil.w} height={kep.mobil.h} />}
          <img src={kep.src} alt={alt} loading="lazy" decoding="async" width={kep.w || undefined} height={kep.h || undefined} />
        </picture>
      </button>
      {b.alairas && <figcaption><Inline c={b.alairas} /></figcaption>}
    </figure>
  );
}

export default function LessonMarkdown({ text }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  const [nyitott, setNyitott] = useState(null);
  const close = useCallback(() => setNyitott(null), []);
  return (
    <div className="lmd">
      {blocks.map((b, k) => {
        if (b.t === 'h2') return <h2 key={k}><Inline c={b.c} /></h2>;
        if (b.t === 'ul') return <ul key={k}>{b.items.map((c, j) => <li key={j}><Inline c={c} /></li>)}</ul>;
        if (b.t === 'ol') return <ol key={k}>{b.items.map((c, j) => <li key={j}><Inline c={c} /></li>)}</ol>;
        if (b.t === 'quote') return <blockquote key={k}><Inline c={b.c} /></blockquote>;
        if (b.t === 'figure') return <Abra key={k} b={b} onOpen={setNyitott} />;
        return <p key={k}><Inline c={b.c} /></p>;
      })}
      {nyitott && <Nagyito kep={nyitott.kep} alt={nyitott.alt} onClose={close} />}
    </div>
  );
}
